import io from 'socket.io-client';
import { FullMessageDto } from '../interfaces/MessageInterfaces';

class WebSocketService {
  private static socket: any | null = null;
  private static reconnectAttempts = 0;
  private static maxReconnectAttempts = 5;
  private static isConnecting = false;

  static connect(): any {
    if (this.socket && this.socket.connected) {
      return this.socket;
    }

    if (this.isConnecting) {
      return this.socket;
    }

    this.isConnecting = true;
    const apiUrl = process.env.REACT_APP_API_URL?.trim() || window.location.origin;
    
    this.socket = io(`${apiUrl}/messages`, {
      forceNew: false,
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
    });

    this.socket.on('connect', () => {
      console.log('Connected to WebSocket server');
      this.reconnectAttempts = 0;
      this.isConnecting = false;
    });

    this.socket.on('disconnect', (reason: string) => {
      console.log('Disconnected from WebSocket server:', reason);
      this.isConnecting = false;
    });

    this.socket.on('connect_error', (error: Error) => {
      console.error('WebSocket connection error:', error);
      this.reconnectAttempts++;
      this.isConnecting = false;
      
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('Max reconnection attempts reached');
      }
    });

    return this.socket;
  }

  static disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnecting = false;
    }
  }

  static joinUserRoom(userGuid: string) {
    const socket = this.getSocket();
    if (socket) {
      console.log('Joining user room:', userGuid);
      socket.emit('join-user-room', userGuid);
    }
  }

  static joinManagerRoom() {
    const socket = this.getSocket();
    if (socket) {
      console.log('Joining manager room');
      socket.emit('join-manager-room');
    }
  }

  static leaveUserRoom(userGuid: string) {
    if (this.socket) {
      console.log('Leaving user room:', userGuid);
      this.socket.emit('leave-user-room', userGuid);
    }
  }

  static leaveManagerRoom() {
    if (this.socket) {
      console.log('Leaving manager room');
      this.socket.emit('leave-manager-room');
    }
  }

  static onNewMessage(callback: (message: FullMessageDto) => void) {
    const socket = this.getSocket();
    if (socket) {
      socket.on('new-message', callback);
    }
  }

  static onNewUserMessage(callback: (message: FullMessageDto) => void) {
    const socket = this.getSocket();
    if (socket) {
      socket.on('new-user-message', callback);
    }
  }

  static onMessageUpdate(callback: (data: { messageGuid: string; updateData: any }) => void) {
    const socket = this.getSocket();
    if (socket) {
      socket.on('message-update', callback);
    }
  }

  static offNewMessage(callback?: (message: FullMessageDto) => void) {
    if (this.socket) {
      this.socket.off('new-message', callback);
    }
  }

  static offNewUserMessage(callback?: (message: FullMessageDto) => void) {
    if (this.socket) {
      this.socket.off('new-user-message', callback);
    }
  }

  static offMessageUpdate(callback?: (data: { messageGuid: string; updateData: any }) => void) {
    if (this.socket) {
      this.socket.off('message-update', callback);
    }
  }

  static isConnected(): boolean {
    return this.socket !== null && this.socket.connected;
  }

  private static getSocket() {
    if (!this.socket || !this.socket.connected) {
      this.connect();
    }
    return this.socket;
  }
}

export default WebSocketService;